import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type TreeApi } from "@/lib/api";

/**
 * Localiza um exemplar ja cadastrado pelo codigo CTA ou pela especie, para abrir
 * a tela de fotos do coletor. A lista e a do projeto da missao ativa; um codigo
 * completo de outro projeto ainda e encontrado pela consulta por codigo.
 */
export default function FieldTreeSearch({ projectId }: { projectId?: number }) {
  const navigate = useNavigate();
  const [trees, setTrees] = useState<TreeApi[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  const [lookup, setLookup] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .trees(projectId)
      .then((rows) => !cancelled && setTrees(rows))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "Erro ao consultar."));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const matches = useMemo(() => {
    if (!trees) return [];
    const q = term.trim().toLowerCase();
    if (!q) return trees.slice(0, 30);
    return trees
      .filter((t) =>
        [t.cta_code, t.species_popular_name, t.species_scientific_name, t.street]
          .some((v) => v?.toLowerCase().includes(q)),
      )
      .slice(0, 30);
  }, [trees, term]);

  const findByCode = async () => {
    const code = term.trim().toUpperCase();
    if (!code) return;
    setLookup(`Procurando ${code}...`);
    try {
      const tree = await api.treeByCode(code);
      navigate(`/coletor/exemplar/${tree.id}`);
    } catch {
      setLookup(`Código ${code} não encontrado no CTA.`);
    }
  };

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (matches.length === 1) navigate(`/coletor/exemplar/${matches[0].id}`);
          else void findByCode();
        }}
        className="flex gap-2"
      >
        <input
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setLookup(null);
          }}
          placeholder="Código CTA ou espécie"
          className="flex-1 min-w-0 px-3 py-3 rounded-xl border border-[#E8EAED] bg-white text-sm"
          autoCapitalize="characters"
        />
        <button type="submit" className="px-4 rounded-xl bg-[#1B6E4F] text-white text-sm font-heading font-bold">
          Buscar
        </button>
      </form>
      {lookup && <p className="text-xs text-[#3F4A52]/70">{lookup}</p>}

      <div className="grid grid-cols-2 gap-2">
        <Link to="/coletor/qrcode" className="py-3 rounded-xl border border-[#E8EAED] bg-white text-center text-sm font-medium text-[#3F4A52]">
          <i className="ri-qr-scan-2-line mr-1" /> Ler QR Code
        </Link>
        <Link to="/coletor/mapa" className="py-3 rounded-xl border border-[#E8EAED] bg-white text-center text-sm font-medium text-[#3F4A52]">
          <i className="ri-map-pin-line mr-1" /> Próximos
        </Link>
      </div>

      {error ? (
        <p className="text-xs text-[#D32F2F]">Não foi possível consultar: {error}</p>
      ) : trees === null ? (
        <p className="text-xs text-[#3F4A52]/60">Carregando exemplares...</p>
      ) : matches.length === 0 ? (
        <p className="text-sm text-[#3F4A52]/70 bg-white border border-dashed border-[#E8EAED] rounded-xl px-4 py-6 text-center">
          {trees.length === 0 ? "Nenhum exemplar cadastrado neste projeto." : "Nenhum exemplar com esse código ou espécie."}
        </p>
      ) : (
        <div className="space-y-2">
          {matches.map((t) => (
            <Link
              key={t.id}
              to={`/coletor/exemplar/${t.id}`}
              className="flex items-center gap-3 p-3 rounded-xl border border-[#E8EAED] bg-white"
            >
              <div className="w-10 h-10 rounded-lg bg-[#E8EAED] flex items-center justify-center flex-shrink-0">
                <i className="ri-leaf-line text-[#1B6E4F] text-lg" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#3F4A52] truncate">
                  {t.species_popular_name || t.species_scientific_name || "Espécie não identificada"}
                </p>
                <p className="text-xs text-[#3F4A52]/50 font-mono truncate">
                  {t.cta_code}
                  {t.street ? ` · ${t.street}` : ""}
                </p>
              </div>
              <i className="ri-camera-line text-[#1B6E4F] text-lg" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
